import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FaMapMarkerAlt, FaPlus, FaUsers, FaSearch } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import { useBusinessUnit } from "../../context/BusinessUnitContext";
import { PageHeader, Panel, StatusBadge, FilterSearch, EmptyState } from "../../components/Portal/ui";
import { getProfiles, getSearch, listSearches } from "../../api/searchApi";
import CandidatesModal from "../../components/AsapProfile/CandidatesModal";
import CreateSearchModal from "../../components/AsapProfile/CreateSearchModal";
import CriteriaForm, {
  skillTypeFor,
} from "../../components/AsapProfile/CriteriaForm";
import ProfileSearchMonthlyChart from "../../components/AsapProfile/ProfileSearchMonthlyChart";
import { scoreColorScheme, SKILL_TYPE_COLOR } from "../../components/AsapProfile/colors";

const buildCardSkillChips = (s) => {
  const required = Array.isArray(s.criteriaSummary?.skillsRequired)
    ? s.criteriaSummary.skillsRequired
    : [];
  const optional = Array.isArray(s.criteriaSummary?.skillsOptional)
    ? s.criteriaSummary.skillsOptional
    : [];
  const typed = {
    technical: s.criteriaSummary?.skillsTechnical || [],
    functional: s.criteriaSummary?.skillsFunctional || [],
    business: s.criteriaSummary?.skillsBusiness || [],
  };
  return [...required, ...optional].map((label) => {
    const type = skillTypeFor(label, typed);
    return {
      label,
      color: SKILL_TYPE_COLOR[type] || "gray",
      type: type || "untyped",
    };
  });
};

const STATUS_COLOR = {
  draft: "gray",
  extracted: "blue",
  validated: "purple",
  queued: "yellow",
  running: "orange",
  completed: "green",
  failed: "red",
};

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString();
  } catch (_) {
    return "-";
  }
};

// Map a backend status_message code (e.g. "pdl_rate_limit",
// "criteria_missing", "finalize_persist_failed") to a user-friendly
// localized string. Returns null for unknown codes so the caller can fall
// back to a generic message — we never render the raw code, since the
// codes leak internal jargon and the underlying data source's name.
const translateFailureReason = (code, t) => {
  if (!code) return null;
  const key = `asapProfile.run.failedReasons.${code}`;
  const translated = t(key, { defaultValue: "" });
  return translated || null;
};

const EMPTY_SEARCH = {
  id: null,
  status: "draft",
  criteriaSummary: {},
};

const AsapProfile = () => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();
  const candidatesModal = useDisclosure();
  const createModal = useDisclosure();

  const [searches, setSearches] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedSearch, setSelectedSearch] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [profilesMeta, setProfilesMeta] = useState({ pdlOffsetLoaded: 0 });
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [filter, setFilter] = useState("");

  const reloadList = useCallback(async () => {
    if (!selectedBusinessUnitId) return null;
    setIsLoadingList(true);
    try {
      const data = await listSearches(
        { page: 1, pageSize: 50 },
        { buId: selectedBusinessUnitId }
      );
      const items = Array.isArray(data?.items) ? data.items : [];
      // Skip the setState (and therefore the cards' re-render) when the
      // server returned the exact same list. Polling otherwise churns the
      // searches array's identity every 5 s.
      setSearches((prev) =>
        prev && JSON.stringify(prev) === JSON.stringify(items) ? prev : items
      );
      return items;
    } catch (err) {
      console.error("Failed to load searches", err);
      toast({
        title: t("asapProfile.errors.loadFailed"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setSearches([]);
      return [];
    } finally {
      setIsLoadingList(false);
      setHasFetched(true);
    }
  }, [selectedBusinessUnitId, toast, t]);

  useEffect(() => {
    if (!isBUContextLoaded || !selectedBusinessUnitId) return;
    reloadList();
  }, [isBUContextLoaded, selectedBusinessUnitId, reloadList]);

  const fetchDetail = useCallback(
    async (id) => {
      if (!id || !selectedBusinessUnitId) return;
      setIsLoadingDetail(true);
      try {
        const data = await getSearch(id, { buId: selectedBusinessUnitId });
        // Keep the prev object identity if nothing material changed.
        // CriteriaForm re-syncs from this prop on every change, so a
        // stable ref is what stops the form from resetting on every poll.
        setSelectedSearch((prev) =>
          prev && JSON.stringify(prev) === JSON.stringify(data) ? prev : data
        );
      } catch (err) {
        console.error("Failed to load search detail", err);
        toast({
          title: t("asapProfile.errors.detailLoadFailed"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setSelectedSearch(null);
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [selectedBusinessUnitId, toast, t]
  );

  useEffect(() => {
    if (selectedId) {
      fetchDetail(selectedId);
    } else {
      setSelectedSearch(null);
      setProfiles([]);
      setProfilesMeta({ pdlOffsetLoaded: 0 });
    }
  }, [selectedId, fetchDetail]);

  // Poll the selected search's detail while it's queued or running so the
  // UI advances to "completed"/"failed" without the user clicking. The
  // typical bg-task happy path completes in 5–20s, so we poll every 5s
  // for the first minute, then back off to 30s — keeps the badge snappy
  // without hammering the API on long-running rate-limit retries.
  useEffect(() => {
    if (!selectedSearch || !selectedBusinessUnitId) return;
    const status = selectedSearch.status;
    if (status !== "queued" && status !== "running") return;
    let elapsed = 0;
    let timer = null;
    let cancelled = false;
    // Await both fetches before scheduling the next tick. Without the
    // awaits, a slow backend (response > 5 s) would let two ticks stack
    // and double-bill the API while the user waits.
    const tick = async () => {
      try {
        await Promise.all([
          fetchDetail(selectedSearch.id),
          reloadList(),
        ]);
      } finally {
        if (!cancelled) {
          elapsed += 1;
          // First ~12 ticks at 5 s; then every 30 s on long-running
          // rate-limit retries.
          const next = elapsed < 12 ? 5000 : 30000;
          timer = setTimeout(tick, next);
        }
      }
    };
    timer = setTimeout(tick, 5000);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [
    selectedSearch?.id,
    selectedSearch?.status,
    selectedBusinessUnitId,
    fetchDetail,
    reloadList,
  ]);

  // Emit a one-shot toast when the polling loop observes a terminal
  // transition (queued/running → completed/failed), so the user is told
  // the search is done without having to refresh.
  const previousStatusRef = useRef(null);
  useEffect(() => {
    const prev = previousStatusRef.current;
    const next = selectedSearch?.status || null;
    previousStatusRef.current = next;
    if (!prev || prev === next) return;
    const wasInFlight = prev === "queued" || prev === "running";
    if (wasInFlight && next === "completed") {
      toast({
        title: t("asapProfile.run.completedToast", {
          defaultValue: "Search completed. View candidates from the list.",
        }),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      // Refresh the list so the card flips from "Running" → "Completed"
      // and the "View candidates" badge appears.
      reloadList();
    } else if (wasInFlight && next === "failed") {
      const reason = selectedSearch?.statusMessage;
      toast({
        title:
          translateFailureReason(reason, t) ||
          t("asapProfile.run.failedToast", {
            defaultValue:
              "Search failed. See the search detail for the reason.",
          }),
        status: "error",
        duration: 7000,
        isClosable: true,
      });
      reloadList();
    }
  }, [selectedSearch?.status, toast, t, reloadList]);

  // Track which search id we last fetched profiles for so the queued →
  // running → completed status flips re-fetch silently instead of flashing
  // the modal's spinner each time.
  const lastFetchedProfilesIdRef = useRef(null);
  useEffect(() => {
    if (!selectedSearch || !selectedBusinessUnitId) return;
    // v2 only persists profiles at "completed" — fetching during "running"
    // is wasted work and risks setting an empty list mid-flight.
    if (selectedSearch.status !== "completed") {
      setProfiles([]);
      setProfilesMeta({ pdlOffsetLoaded: 0 });
      lastFetchedProfilesIdRef.current = null;
      return;
    }
    let cancelled = false;
    const isFirstFetchForId =
      lastFetchedProfilesIdRef.current !== selectedSearch.id;
    if (isFirstFetchForId) setIsLoadingProfiles(true);
    (async () => {
      try {
        const data = await getProfiles(selectedSearch.id, {
          buId: selectedBusinessUnitId,
        });
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        setProfiles((prev) =>
          prev && JSON.stringify(prev) === JSON.stringify(items) ? prev : items
        );
        setProfilesMeta({
          pdlOffsetLoaded: data?.pdlOffsetLoaded ?? 0,
          topScore: data?.topScore ?? null,
          loadMoreCount: data?.loadMoreCount ?? 0,
          maxLoadMorePerSearch: data?.maxLoadMorePerSearch ?? null,
          exhausted: data?.exhausted ?? false,
        });
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load search profiles", err);
        toast({
          title: t("asapProfile.errors.profilesLoadFailed"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        if (!cancelled) {
          // Mark this id as fetched in BOTH success and error branches so
          // a transient error doesn't make the next poll re-flash the
          // modal spinner on every retry.
          lastFetchedProfilesIdRef.current = selectedSearch.id;
          if (isFirstFetchForId) setIsLoadingProfiles(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    selectedSearch?.id,
    selectedSearch?.status,
    selectedBusinessUnitId,
    toast,
    t,
  ]);

  const handleRunCompleted = useCallback(
    (result) => {
      const items = Array.isArray(result?.items) ? result.items : [];
      setProfiles(items);
      setProfilesMeta({
        pdlOffsetLoaded: result?.pdlOffsetLoaded ?? items.length,
        topScore: result?.topScore ?? null,
        loadMoreCount: result?.loadMoreCount ?? 0,
        maxLoadMorePerSearch: result?.maxLoadMorePerSearch ?? null,
        exhausted: result?.exhausted ?? false,
      });
      fetchDetail(selectedSearch?.id);
      reloadList();
      candidatesModal.onOpen();
    },
    [fetchDetail, reloadList, selectedSearch?.id, candidatesModal]
  );

  const handleLoadMoreAppended = useCallback(
    (result) => {
      const newOnes = Array.isArray(result?.items) ? result.items : [];
      // Append the new batch at the bottom of the list, in the order the
      // backend returned it. The sourcing algorithm already relaxes by skill
      // coverage first, so each "Load 12 more" batch is naturally lower-scored
      // than the one before. We intentionally do NOT re-rank the new profiles
      // into the existing list: the first batch is the set the user already
      // reviewed/contacted, and reshuffling it would be disruptive.
      setProfiles((prev) => [...prev, ...newOnes]);
      setProfilesMeta((prev) => ({
        pdlOffsetLoaded: result?.pdlOffsetLoaded ?? 0,
        topScore: result?.topScore ?? prev?.topScore ?? null,
        loadMoreCount: result?.loadMoreCount ?? prev?.loadMoreCount ?? 0,
        maxLoadMorePerSearch:
          result?.maxLoadMorePerSearch ?? prev?.maxLoadMorePerSearch ?? null,
        // The load-more response carries `exhausted: true` once the ladder
        // has no further rungs, which flips the button to the terminal
        // "no more profiles" message.
        exhausted: result?.exhausted ?? prev?.exhausted ?? false,
      }));
      reloadList();
    },
    [reloadList]
  );

  const handleProfileUpdated = useCallback((updatedProfile) => {
    setProfiles((prev) =>
      prev.map((p) => (p.id === updatedProfile.id ? updatedProfile : p))
    );
  }, []);

  const handleSourceCreated = useCallback(
    async (newId) => {
      await reloadList();
      if (newId) setSelectedId(newId);
    },
    [reloadList]
  );

  const handleSearchUpdated = useCallback(
    (updated) => {
      setSelectedSearch(updated);
      reloadList();
    },
    [reloadList]
  );

  const handleSearchForked = useCallback(
    (created) => {
      // Save & re-run on a completed search now creates a new SearchRequest
      // (preserving the original's profiles). Switch the selection so the
      // detail panel shows the new fork — the user expects to see it run.
      setSelectedSearch(created);
      setSelectedId(created.id);
      reloadList();
    },
    [reloadList]
  );

  const handleViewCandidatesFromCard = (e, search) => {
    e.stopPropagation();
    setSelectedId(search.id);
    candidatesModal.onOpen();
  };

  const filteredSearches = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return searches;
    return searches.filter((s) => {
      const haystack = `${s.title || ""} ${s.client || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [searches, filter]);

  const detailHeading = useMemo(() => {
    if (!selectedSearch) return t("asapProfile.detail.newSearchTitle");
    return selectedSearch.title || t("asapProfile.detail.title");
  }, [selectedSearch, t]);

  const renderRightPanel = () => {
    // Only blank the panel for the very first load of a selection. Once we
    // have a selectedSearch, polling re-fetches stay silent — the in-progress
    // banner + status badge already convey "still working", and keeping the
    // form mounted preserves the user's in-flight edits.
    if (isLoadingDetail && !selectedSearch) {
      return (
        <Center py={10}>
          <Spinner />
        </Center>
      );
    }

    return (
      <VStack align="stretch" spacing={4} data-testid="detail-content">
        <HStack>
          <Heading
            as="h3"
            fontSize="14px"
            letterSpacing="-0.02em"
            color="portal.text"
            sx={{ marginBottom: "0 !important" }}
          >
            {detailHeading}
          </Heading>
          {selectedSearch && (
            <StatusBadge
              label={t(`asapProfile.status.${selectedSearch.status}`, {
                defaultValue: selectedSearch.status,
              })}
              colorScheme={STATUS_COLOR[selectedSearch.status] || "gray"}
            />
          )}
        </HStack>

        {!selectedSearch && (
          <Box bg="portal.soft" borderWidth="1px" borderColor="portal.line" p="12px" borderRadius="10px">
            <Text
              fontSize="12px"
              color="portal.muted"
              sx={{ marginBottom: "0 !important" }}
            >
              {t("asapProfile.detail.disabledMessage")}
            </Text>
          </Box>
        )}

        {selectedSearch?.status === "draft" && (
          <Center py={6}>
            <HStack>
              <Spinner size="sm" />
              <Text sx={{ marginBottom: "0 !important" }}>
                {t("asapProfile.detail.extractingMessage")}
              </Text>
            </HStack>
          </Center>
        )}

        {(selectedSearch?.status === "queued" ||
          selectedSearch?.status === "running") && (
          <Box
            bg="orange.50"
            p={3}
            borderRadius="md"
            data-testid="search-in-progress-banner"
          >
            <HStack>
              <Spinner size="sm" color="orange.500" />
              <Text
                fontSize="sm"
                color="orange.800"
                sx={{ marginBottom: "0 !important" }}
              >
                {t("asapProfile.detail.inProgressMessage", {
                  defaultValue:
                    "Searching candidates… this can take up to a minute.",
                })}
              </Text>
            </HStack>
          </Box>
        )}

        {selectedSearch?.status === "failed" && (
          <Box bg="red.50" p={3} borderRadius="md">
            <Text
              fontSize="sm"
              color="red.700"
              sx={{ marginBottom: "0 !important" }}
            >
              {translateFailureReason(selectedSearch.statusMessage, t) ||
                t("asapProfile.detail.extractFailedMessage")}
            </Text>
          </Box>
        )}

        <CriteriaForm
          search={selectedSearch || EMPTY_SEARCH}
          buId={selectedBusinessUnitId}
          disabled={!selectedSearch || selectedSearch.status === "draft"}
          onSearchUpdated={handleSearchUpdated}
          onSearchForked={handleSearchForked}
          onRunCompleted={handleRunCompleted}
          onViewCandidates={candidatesModal.onOpen}
        />
      </VStack>
    );
  };

  const renderCard = (s) => {
    const isSelected = selectedId === s.id;
    const chips = buildCardSkillChips(s);
    return (
      <Box
        key={s.id}
        role="button"
        tabIndex={0}
        cursor="pointer"
        textAlign="left"
        borderWidth="1px"
        borderColor={isSelected ? "portal.primary" : "portal.line"}
        bg={isSelected ? "portal.active" : "portal.panel"}
        borderRadius="10px"
        p="14px"
        transition="box-shadow .15s, background .15s"
        _hover={{ boxShadow: "kpi", bg: isSelected ? "portal.active" : "portal.soft" }}
        onClick={() => setSelectedId(isSelected ? null : s.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setSelectedId(isSelected ? null : s.id);
          }
        }}
        data-testid={`search-card-${s.id}`}
      >
        <HStack justify="space-between" align="start" spacing={3}>
          <Box flex="1" minW="0">
            <HStack
              spacing={1.5}
              align="center"
              sx={{ marginBottom: "2px !important" }}
            >
              <Text
                fontSize="13px"
                fontWeight="700"
                color="portal.text"
                noOfLines={1}
                sx={{ marginBottom: "0 !important" }}
                data-testid={`search-card-title-${s.id}`}
              >
                {s.title || t("asapProfile.list.untitled")}
              </Text>
              {s.parentSearchId && (
                <Badge
                  colorScheme="purple"
                  variant="subtle"
                  fontSize="2xs"
                  data-testid={`search-card-fork-badge-${s.id}`}
                  title={t("asapProfile.list.forkBadgeTooltip", {
                    parent: s.parentSearchId,
                  })}
                >
                  {t("asapProfile.list.forkBadge", {
                    parent: s.parentSearchId,
                  })}
                </Badge>
              )}
            </HStack>
            <HStack
              spacing={1}
              align="center"
              sx={{ marginBottom: "2px !important" }}
            >
              <Text
                fontSize="11px"
                color="portal.muted"
                noOfLines={1}
                sx={{ marginBottom: "0 !important" }}
              >
                {s.client || "-"}
              </Text>
              {s.geoLabel && (
                <>
                  <Text
                    fontSize="11px"
                    color="portal.line"
                    sx={{ marginBottom: "0 !important" }}
                  >
                    ·
                  </Text>
                  <Box as={FaMapMarkerAlt} color="portal.muted" boxSize="10px" />
                  <Text
                    fontSize="11px"
                    color="portal.muted"
                    noOfLines={1}
                    sx={{ marginBottom: "0 !important" }}
                    data-testid={`search-card-geo-${s.id}`}
                  >
                    {s.geoLabel}
                  </Text>
                </>
              )}
            </HStack>
            <Text
              fontSize="11px"
              color="portal.muted"
              sx={{ marginBottom: "0 !important" }}
            >
              {formatDate(s.createdAt)}
              {s.seniority
                ? ` · ${t(`asapProfile.seniority.${s.seniority}`, {
                    defaultValue: s.seniority,
                  })}`
                : ""}
            </Text>
          </Box>
          <VStack align="end" spacing={1}>
            {s.topScore !== null && s.topScore !== undefined && (
              <Badge
                colorScheme={scoreColorScheme(s.topScore)}
                variant="solid"
                borderRadius="md"
                px={2}
                data-testid={`search-card-score-${s.id}`}
              >
                {s.topScore}
              </Badge>
            )}
            <StatusBadge
              label={t(`asapProfile.status.${s.status}`, {
                defaultValue: s.status,
              })}
              colorScheme={STATUS_COLOR[s.status] || "gray"}
            />
          </VStack>
        </HStack>
        {chips.length > 0 && (
          <Wrap mt={2} spacing={2}>
            {chips.map((chip) => (
              <WrapItem key={`${s.id}:${chip.label}`}>
                <Badge
                  colorScheme={chip.color}
                  variant="subtle"
                  data-skill-type={chip.type}
                >
                  {chip.label}
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
        )}
        {s.status === "completed" && (
          <HStack mt="12px" justify="flex-end">
            <Button
              size="xs"
              h="25px"
              fontSize="11px"
              leftIcon={<FaUsers />}
              variant="secondary"
              onClick={(e) => handleViewCandidatesFromCard(e, s)}
              data-testid={`search-card-view-${s.id}`}
            >
              {t("asapProfile.list.cardViewCandidates")}
            </Button>
          </HStack>
        )}
      </Box>
    );
  };

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%" display="flex" flexDirection="column">
      <PageHeader
        moduleCode="PF"
        eyebrow={t("asapProfile.eyebrow", "ASAP Profile")}
        title={t("asapProfile.pageTitle")}
        actions={
          <Button
            variant="primary"
            size="sm"
            h="32px"
            px="12px"
            fontSize="12px"
            leftIcon={<FaPlus />}
            onClick={createModal.onOpen}
            data-testid="asap-profile-new-search"
          >
            {t("asapProfile.list.newSearch")}
          </Button>
        }
      />

      <Box p="20px" flex="1" minH="0" display="flex" flexDirection="column">
        <Flex gap="12px" flex="1" minH="0" direction={{ base: "column", md: "row" }}>
          {/* Left — search cards */}
          <Box
            flex={{ base: "1 1 100%", md: "0 0 38%" }}
            minW={{ md: "320px" }}
            minH="0"
            display="flex"
          >
            <Panel
              w="100%"
              h="100%"
              title={t("asapProfile.list.title")}
              count={searches.length}
              filters={
                <FilterSearch
                  placeholder={t("asapProfile.list.searchPlaceholder")}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  data-testid="asap-profile-list-filter"
                />
              }
              bodyProps={{ p: "12px" }}
            >
              <Box data-testid="asap-profile-list-block">
                {isLoadingList && !hasFetched ? (
                  <Center py="40px">
                    <Spinner color="portal.primary" />
                  </Center>
                ) : searches.length === 0 ? (
                  <EmptyState
                    icon={<FaSearch />}
                    title={hasFetched ? t("asapProfile.emptyList") : t("asapProfile.list.loading")}
                  />
                ) : filteredSearches.length === 0 ? (
                  <EmptyState
                    icon={<FaSearch />}
                    title={t("asapProfile.list.noMatch")}
                  />
                ) : (
                  <VStack align="stretch" spacing="12px">
                    {filteredSearches.map(renderCard)}
                  </VStack>
                )}
              </Box>
            </Panel>
          </Box>

          {/* Right — search details */}
          <Box flex="1" minH="0" display="flex">
            <Panel w="100%" h="100%" bodyProps={{ p: "16px" }}>
              <Box data-testid="asap-profile-detail-block">
                <ProfileSearchMonthlyChart buId={selectedBusinessUnitId} />
                <Heading
                  as="h3"
                  fontSize="14px"
                  letterSpacing="-0.02em"
                  color="portal.text"
                  mt="16px"
                  sx={{ marginBottom: "12px !important" }}
                >
                  {t("asapProfile.detail.title")}
                </Heading>
                {renderRightPanel()}
              </Box>
            </Panel>
          </Box>
        </Flex>
      </Box>

      <CreateSearchModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        buId={selectedBusinessUnitId}
        onCreated={handleSourceCreated}
      />

      <CandidatesModal
        isOpen={candidatesModal.isOpen}
        onClose={candidatesModal.onClose}
        search={selectedSearch}
        buId={selectedBusinessUnitId}
        profiles={profiles}
        profilesMeta={profilesMeta}
        isLoadingProfiles={isLoadingProfiles}
        onProfileUpdated={handleProfileUpdated}
        onLoadMoreAppended={handleLoadMoreAppended}
      />
    </Box>
  );
};

export default AsapProfile;
